/**
 * DOM stage: the classic game-dev "design-resolution" pattern.
 *
 * Every visible element is authored against a FIXED 1920x1080 base. The root
 * stage div is sized exactly 1920x1080 in CSS pixels, and a CSS transform
 * scales it uniformly to whatever viewport we land on — same S on both axes
 * so proportions never warp. The viewport is cleared to black so letter/pillar
 * boxing appears naturally when the viewport aspect doesn't match 16:9.
 *
 * Why this exists: OBS browser sources run at 1920x1080 by default, but
 * Playwright playtests, multi-monitor setups, and dashboard previews all
 * render at arbitrary sizes. Instead of every overlay doing its own media-query
 * responsive dance (and inevitably drifting out of alignment with each other),
 * the whole stage scales as one unit — geometry is designed once, in pixels,
 * and the transform handles every other viewport size.
 *
 * Math:
 *   S = min(vw / 1920, vh / 1080)
 *   offsetX = (vw - 1920 * S) / 2
 *   offsetY = (vh - 1080 * S) / 2
 *   transform: translate(offsetX, offsetY) scale(S)
 *   transform-origin: 0 0
 *
 * Children of `stage.el` position themselves inside a 1920x1080 coordinate
 * space (absolute pixels) — no viewport units allowed inside the stage.
 *
 * The Three.js canvas lives *inside* the stage too: the WebGL renderer draws
 * at an internal 1920x1080 framebuffer (via renderer.setSize), the canvas CSS
 * stretches to 100% of its parent, and the stage transform does the final
 * viewport scaling. Net effect: one consistent ruler for DOM + WebGL.
 */

export const STAGE_W = 1920;
export const STAGE_H = 1080;

export interface DomStageHandle {
  /** The stage element. Append DOM overlays here instead of document.body. */
  el: HTMLDivElement;
  /** Recompute scale + offset. Called automatically on window/documentElement resize. */
  resize: () => void;
  dispose: () => void;
}

const BG_ID = 'sg-stage-bg';
const STAGE_ID = 'sg-stage';

function ensureBaseStyles(): void {
  if (document.getElementById('sg-stage-style')) return;
  const s = document.createElement('style');
  s.id = 'sg-stage-style';
  s.textContent = `
    #${BG_ID} {
      position: fixed;
      inset: 0;
      background: #000;
      overflow: hidden;
      z-index: 0;
    }
    #${STAGE_ID} {
      position: fixed;
      left: 0;
      top: 0;
      width: ${STAGE_W}px;
      height: ${STAGE_H}px;
      transform-origin: 0 0;
      /* Backing color so the stage itself is never transparent over the letterbox. */
      background: transparent;
      /* Children use position:absolute against this fixed-size box. */
      overflow: hidden;
      z-index: 1;
    }
  `;
  document.head.appendChild(s);
}

export interface DomStageOpts {
  /** Where to mount the letterbox backdrop + stage. Defaults to document.body. */
  mount?: HTMLElement;
}

/**
 * Create the DOM stage. Attaches a fixed-position letterbox backdrop and a
 * 1920x1080 stage container to `mount` (default: document.body). The returned
 * handle's `el` is what overlays should mount into.
 */
export function createDomStage(opts: DomStageOpts = {}): DomStageHandle {
  ensureBaseStyles();
  const mount = opts.mount ?? document.body;

  // Letterbox backdrop — solid black, behind everything, covers the viewport
  // so OBS / browsers composite black bars when aspect mismatches.
  const bg = document.createElement('div');
  bg.id = BG_ID;
  bg.setAttribute('data-overlay', 'stage-bg');
  mount.appendChild(bg);

  // The stage itself. Fixed 1920x1080 in CSS pixels; scaled by transform.
  const el = document.createElement('div');
  el.id = STAGE_ID;
  el.setAttribute('data-overlay', 'stage');
  el.setAttribute('data-stage-w', String(STAGE_W));
  el.setAttribute('data-stage-h', String(STAGE_H));
  mount.appendChild(el);

  let lastScale = 0;
  let lastOffsetX = 0;
  let lastOffsetY = 0;

  function resize(): void {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const s = Math.min(vw / STAGE_W, vh / STAGE_H);
    const ox = Math.round((vw - STAGE_W * s) / 2);
    const oy = Math.round((vh - STAGE_H * s) / 2);
    if (s === lastScale && ox === lastOffsetX && oy === lastOffsetY) return;
    lastScale = s;
    lastOffsetX = ox;
    lastOffsetY = oy;
    el.style.transform = `translate(${ox}px, ${oy}px) scale(${s})`;
    // Expose the current scale to any interested party (e.g. pointer-hit code).
    (window as unknown as { __sj_stage_scale?: number }).__sj_stage_scale = s;
  }

  // Initial compute + listeners.
  resize();
  window.addEventListener('resize', resize);
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(() => resize());
    ro.observe(document.documentElement);
  }

  return {
    el,
    resize,
    dispose() {
      window.removeEventListener('resize', resize);
      if (ro) ro.disconnect();
      el.remove();
      bg.remove();
    },
  };
}
